import { z } from 'zod';
import { deviceIdSchema } from './auth.js';
import { paginationQuerySchema } from './pagination.js';
/**
 * Device binding (D93-D95). IMEI and MAC addresses are not read: Android has
 * blocked IMEI for normal apps since Android 10, iOS never allowed it, and both
 * return the constant 02:00:00:00:00:00 for MAC. Instead the app generates its
 * own id on first launch and keeps it in secure storage.
 *
 * That id is NOT a hardware identifier: reinstalling the app or wiping the
 * phone produces a new one, so a legitimate agent can trigger the "new device"
 * alert. The flow is built to make that easy to resolve, not to punish it.
 */
/** Sent by the phone with every request, next to the bearer token. */
export const DEVICE_ID_HEADER = 'x-device-id';
/** What the platform does allow us to know about the phone. Everything is optional. */
export const deviceInfoSchema = z.object({
    platform: z.enum(['android', 'ios', 'web', 'unknown']).default('unknown'),
    model: z.string().max(100).nullable().default(null),
    manufacturer: z.string().max(100).nullable().default(null),
    osVersion: z.string().max(50).nullable().default(null),
    appVersion: z.string().max(40).nullable().default(null),
    /** Android Build.FINGERPRINT (identifies the OS build, not the unit). */
    buildFingerprint: z.string().max(300).nullable().default(null),
    deviceName: z.string().max(100).nullable().default(null),
});
export const DEVICE_STATUSES = ['APPROVED', 'PENDING', 'REJECTED'];
/** OPEN until an admin looks at it; a pending device keeps working meanwhile (D94). */
export const DEVICE_ALERT_STATUSES = ['NONE', 'OPEN', 'RESOLVED'];
export const userDeviceDtoSchema = z.object({
    id: z.string(),
    userId: z.string(),
    userFullName: z.string(),
    deviceId: z.string(),
    status: z.enum(DEVICE_STATUSES),
    alertStatus: z.enum(DEVICE_ALERT_STATUSES),
    platform: z.string(),
    model: z.string().nullable(),
    manufacturer: z.string().nullable(),
    osVersion: z.string().nullable(),
    appVersion: z.string().nullable(),
    buildFingerprint: z.string().nullable(),
    deviceName: z.string().nullable(),
    firstSeenAt: z.string(),
    lastSeenAt: z.string(),
    decidedBy: z.object({ id: z.string(), fullName: z.string() }).nullable(),
    decidedAt: z.string().nullable(),
    decisionNote: z.string().nullable(),
    /** True for the device the account was first bound to. */
    isFirst: z.boolean(),
});
export const deviceDecisionRequestSchema = z.object({
    decision: z.enum(['APPROVE', 'REJECT', 'DISMISS_ALERT']),
    note: z.string().trim().max(500).nullable().default(null),
});
export const devicePolicyRequestSchema = z.object({ requireApproval: z.boolean() });
export const userDevicesResponseSchema = z.object({
    requireApproval: z.boolean(),
    devices: z.array(userDeviceDtoSchema),
});
export const deviceAlertsQuerySchema = paginationQuerySchema.extend({ status: z.enum(['OPEN', 'RESOLVED']).default('OPEN') });
/** Device id as sent in the header: same rules as the login field; anything else is ignored, never an error. */
export function parseDeviceIdHeader(value) {
    if (typeof value !== 'string')
        return null;
    const parsed = deviceIdSchema.safeParse(value);
    return parsed.success ? parsed.data : null;
}
//# sourceMappingURL=devices.js.map